import type { QueryClient } from "@tanstack/react-query";
import pDebounce from "p-debounce";

import { useMeuPlayState } from "@src/states/meuplay";
import { useOfflineModeState } from "@src/states/offline_mode";
import { usePlayerState } from "@src/states/player";

import { player } from "@src/services/player";
import { factory } from "@src/services/radioco";

import { isConnected } from "@src/utils/checkConnection";
import { getFileName } from "@src/utils/getFileName";
import { OFFLINE_SPOT_URL } from "@src/services/player/constants";
import { exitOfflineMode } from "@src/utils/offline/exitOfflineMode";
import { getNextOfflineUrl } from "@src/utils/offline/getNextOfflineUrl";
import { tryExitOfflineMode } from "@src/utils/offline/tryExitOfflineMode";

export const playOfflineUrl = pDebounce(_playOfflineUrl, 500, {
  before: true,
});

async function _playOfflineUrl(
  playingOrPaused: "playing" | "paused" | null,
  offlineUrl: string,
  queryClient: QueryClient,
) {
  console.log("playOfflineUrl", getFileName(offlineUrl));

  // Antes de tocar o offline_spot, verifica se voltou a internet.
  // Se sim, encerra o modo offline em vez de tocar a vinheta.
  if (offlineUrl.includes(OFFLINE_SPOT_URL)) {
    const exited = await tryExitOfflineMode(queryClient, playingOrPaused);
    if (exited) return;
  }

  usePlayerState.setState({
    currentTrackId: "offline",
    currentTrack: {
      artist: "Modo Offline",
      title: getFileName(offlineUrl),
      artwork_small: "/offline.png",
      artwork_large: "/offline.png",
    },
  });

  player.removeTrack("offline").then(() => {
    player.addTrack("offline", offlineUrl, false);

    let nextOfflineUrl = offlineUrl;
    player.setExtraEventListener("offline", "playing", () => {
      useOfflineModeState.setState((state) => {
        const newPlayedUrls = new Set(state.playedUrls);
        newPlayedUrls.add(offlineUrl);
        return { playedUrls: newPlayedUrls };
      });

      if (nextOfflineUrl === offlineUrl) {
        nextOfflineUrl = getNextOfflineUrl();
        usePlayerState.setState({
          nextTrack: {
            artist: "Modo Offline",
            title: getFileName(nextOfflineUrl),
            artwork_small: "/offline.png",
            artwork_large: "/offline.png",
          },
        });
      }
    });

    player.setExtraEventListener("offline", "ended", () => {
      console.log("Offline track ended.");
      const playNextOfflineTrack = () => {
        // Toca a próxima música offline
        playOfflineUrl("playing", nextOfflineUrl, queryClient);
      };
      isConnected()
        .then((isOnline) => {
          const emoji = isOnline ? "🟢" : "🟡";
          console.log(emoji, "isOnline=", isOnline);
          if (useOfflineModeState.getState().isOnline !== isOnline) {
            console.log(`[playOfflineUrl] isOnline set to ${isOnline}`);
            useOfflineModeState.setState({ isOnline });
          }
          const radio = useMeuPlayState.getState().radio;
          if (isOnline && radio) {
            const radioco = factory(radio.streaming_id);
            radioco
              .isOnAir(false)
              .then((onAir) => {
                const emoji = onAir ? "🟢" : "🟡";
                console.log(emoji, "onAir=", onAir);
                if (usePlayerState.getState().onAir !== onAir) {
                  usePlayerState.setState({ onAir });
                }
                if (onAir) {
                  // Terminou de tocar a música offline, e está online
                  exitOfflineMode("playing", queryClient);
                } else {
                  playNextOfflineTrack();
                }
              })
              .catch((error) => {
                const emoji = "🔴";
                console.log(emoji, "onAir=", error);
                playNextOfflineTrack();
              });
          } else {
            playNextOfflineTrack();
          }
        })
        .catch((error) => {
          const emoji = "🔴";
          console.log(emoji, "isOnline=", error);
          playNextOfflineTrack();
        });
    });

    player.setExtraEventListener("offline", "loadstart", () => {
      setTimeout(() => {
        const { currentTrackId, tracks } = usePlayerState.getState();
        const isOfflinePlaying = currentTrackId === "offline";
        if (
          isOfflinePlaying &&
          tracks["offline"].log[tracks["offline"].log.length - 1] ===
            "loadstart"
        ) {
          player.setError("offline", "Offline track stuck in loadstart.");
        }
      }, 1_000);
    });

    player.setExtraEventListener("offline", "error", () => {
      const currentTrackId = usePlayerState.getState().currentTrackId;
      const isOfflinePlaying = currentTrackId === "offline";
      if (isOfflinePlaying) {
        console.warn("Offline track error. Playing next offline track.");
        playOfflineUrl("playing", nextOfflineUrl, queryClient);
      }
    });

    player.unlock("playOfflineUrl");
    console.log(`playOfflineUrl ${playingOrPaused}`);
    if (playingOrPaused === "playing") {
      player.play("offline");
    }
  });
}
